import { useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import { ERROR_TYPES } from '../upload.constants';

export const useFileProcessing = () => {
  const [isParsing, setIsParsing] = useState(false);
  const [parsingProgress, setParsingProgress] = useState(0);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);

  const parseExcelFile = (file: File) => {
    setIsParsing(true);
    setParsingProgress(0);

    const reader = new FileReader();
    reader.onprogress = e => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded / e.total) * 100);
        setParsingProgress(progress);
      }
    };

    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (e.target?.result) {
        const data = new Uint8Array(e.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        const headers = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
        })[0] as string[];

        const cleanedHeaders = headers.map(header => header.trim());

        const cleanedJsonData = jsonData.map((row: any) => {
          const cleanedRow: { [key: string]: any } = {};
          Object.keys(row).forEach(key => {
            cleanedRow[key.trim()] = row[key];
          });
          return cleanedRow;
        });

        setParsedHeaders(cleanedHeaders);
        setParsedData(cleanedJsonData);
        setIsParsing(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };
  const exportToExcel = (data: any[], headers: string[], errorMap: any, operation: any) => {
    if (data.length === 0) {
      return;
    }

    const wb = XLSX.utils.book_new();
    const exportData = [headers];

    data.forEach(row => {
      const rowData = headers.map(field => row[field] ?? '');
      exportData.push(rowData);
    });

    const ws = XLSX.utils.aoa_to_sheet(exportData);

    Object.entries(errorMap).forEach(([rowIndexStr, fieldErrors]) => {
      const rowIndex = parseInt(rowIndexStr, 10);
      const excelRowIndex = rowIndex;

      Object.entries(fieldErrors).forEach(([field, errorType]) => {
        const colIndex = headers.indexOf(field);

        if (colIndex !== -1) {
          const cellAddress = XLSX.utils.encode_cell({ r: excelRowIndex, c: colIndex });

          if (!ws[cellAddress]) {
            ws[cellAddress] = { t: 's', v: '' };
          }

          const errorColor = ERROR_TYPES[errorType]?.color;

          if (errorColor) {
            ws[cellAddress].s = {
              fill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { rgb: errorColor },
              },
            };
          }
        }
      });
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Données Validées');
    XLSX.writeFile(wb, `validation_${operation.label}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const generateCorrectedExcel = (data: any[], headers: string[], errorMap: any, operation: any) => {
    if (data.length === 0) {
      return null;
    }

    const wb = XLSX.utils.book_new();
    const exportData = [headers];

    data.forEach(row => {
      const rowData = headers.map(field => row[field] ?? '');
      exportData.push(rowData);
    });

    const ws = XLSX.utils.aoa_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, 'Données Corrigées');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };

  return {
    isParsing,
    parsingProgress,
    parsedData,
    parsedHeaders,
    parseExcelFile,
    exportToExcel,
    generateCorrectedExcel,
    setParsedData,
  };
};
